import * as React from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, TextField, Typography } from '@mui/material';
import { getTourMarketingRewards, updateTourMarketingRewards } from '../Api';
import TourMarketingRewardsDto from '../models/dtos/TourMarketingRewardsDto';

/** Accepts "3.5" or "3,5" (common with RO keyboard / browser locale). */
function parseLocaleFloat(raw: string): number {
    const normalized = raw.trim().replace(/\s/g, '').replace(',', '.');
    return parseFloat(normalized);
}

function parseLocaleInt(raw: string): number {
    const t = raw.trim().replace(/\s/g, '');
    if (t.includes(',') || t.includes('.')) return Number.NaN;
    return parseInt(t, 10);
}

export default function TourMarketingPage() {
    const [loading, setLoading] = React.useState(true);
    const [skipBetcoins, setSkipBetcoins] = React.useState<string>('0');
    const [completeBonusBetcoins, setCompleteBonusBetcoins] = React.useState<string>('0');
    const [demoStakeBetcoins, setDemoStakeBetcoins] = React.useState<string>('1000');
    const [coefficientWin1, setCoefficientWin1] = React.useState<string>('2.2');
    const [coefficientDraw, setCoefficientDraw] = React.useState<string>('3.5');
    const [coefficientWin2, setCoefficientWin2] = React.useState<string>('2.5');
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [dialogTitle, setDialogTitle] = React.useState('');
    const [dialogMsg, setDialogMsg] = React.useState('');
    const [dialogHint, setDialogHint] = React.useState('');

    const applyDto = (dto: TourMarketingRewardsDto | null) => {
        if (dto != null) {
            setSkipBetcoins(String(dto.skipBetcoins));
            setCompleteBonusBetcoins(String(dto.completeBonusBetcoins));
            setDemoStakeBetcoins(String(dto.demoStakeBetcoins ?? 1000));
            setCoefficientWin1(String(dto.coefficientWin1 ?? 2.2));
            setCoefficientDraw(String(dto.coefficientDraw ?? 3.5));
            setCoefficientWin2(String(dto.coefficientWin2 ?? 2.5));
        }
    };

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const dto = await getTourMarketingRewards();
            if (!cancelled) {
                applyDto(dto);
                setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const openDialog = (title: string, message: string, hint?: string) => {
        setDialogTitle(title);
        setDialogMsg(message);
        setDialogHint(hint ?? '');
        setDialogOpen(true);
    };

    const onSave = async () => {
        const skip = parseLocaleInt(skipBetcoins);
        const complete = parseLocaleInt(completeBonusBetcoins);
        const demoStake = parseLocaleInt(demoStakeBetcoins);
        const c1 = parseLocaleFloat(coefficientWin1);
        const cd = parseLocaleFloat(coefficientDraw);
        const c2 = parseLocaleFloat(coefficientWin2);
        if (Number.isNaN(skip) || Number.isNaN(complete) || skip < 0 || complete < 0) {
            openDialog('Error', 'Enter non-negative whole numbers for skip and complete tour betcoins.');
            return;
        }
        if (Number.isNaN(demoStake) || demoStake < 0) {
            openDialog('Error', 'Demo stake must be a non-negative whole number.');
            return;
        }
        if (Number.isNaN(c1) || Number.isNaN(cd) || Number.isNaN(c2)) {
            openDialog('Error', 'Coefficients must be valid numbers (you can use 3.5 or 3,5).');
            return;
        }
        if (c1 < 1 || c1 > 1000 || cd < 1 || cd > 1000 || c2 < 1 || c2 > 1000) {
            openDialog('Error', 'Each coefficient must be between 1.0 and 1000.0 (analytics API rule).');
            return;
        }
        const payload: TourMarketingRewardsDto = {
            skipBetcoins: skip,
            completeBonusBetcoins: complete,
            demoStakeBetcoins: demoStake,
            coefficientWin1: c1,
            coefficientDraw: cd,
            coefficientWin2: c2
        };
        const ok = await updateTourMarketingRewards(payload);
        if (!ok) {
            openDialog('Error', 'Failed to save. Check server logs and API configuration.');
            return;
        }
        const dto = await getTourMarketingRewards();
        applyDto(dto);
        openDialog(
            'Saved',
            'Your tour reward settings were saved to analytics.',
            'For betcoins to be credited on real devices, the main BetUp API needs betup.analytics.statsUrl and betup.analytics.statsApiKey set (it reads this config from there).'
        );
    };

    return (
        <Stack gap={3} sx={{ mx: 5, my: 5 }}>
            <Typography variant="h4">Tour rewards (Android onboarding)</Typography>
            <Typography variant="body2" color="text.secondary">
                Betcoins credited when the user skips the tour vs completes it. Use 0 for no reward for that path.
                Demo stake and Win1 / Draw / Win2 coefficients drive the in-app demo match and the extra demo winnings
                credited on tutorial completion (together with the complete bonus). Values are stored in analytics MongoDB;
                the BetUp API reads them when processing tutorial completion.
            </Typography>
            {loading ? (
                <Box><CircularProgress /></Box>
            ) : (
                <Grid container spacing={2} maxWidth="sm">
                    <Grid item xs={12}>
                        <TextField
                            label="Skip tour — betcoins"
                            type="number"
                            fullWidth
                            value={skipBetcoins}
                            onChange={(e) => setSkipBetcoins(e.target.value)}
                            inputProps={{ min: 0 }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Complete tour — bonus betcoins"
                            type="number"
                            fullWidth
                            value={completeBonusBetcoins}
                            onChange={(e) => setCompleteBonusBetcoins(e.target.value)}
                            inputProps={{ min: 0 }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Demo match — stake (betcoins)"
                            type="number"
                            fullWidth
                            value={demoStakeBetcoins}
                            onChange={(e) => setDemoStakeBetcoins(e.target.value)}
                            inputProps={{ min: 0 }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Coefficient Win 1"
                            type="number"
                            fullWidth
                            value={coefficientWin1}
                            onChange={(e) => setCoefficientWin1(e.target.value)}
                            inputProps={{ min: 1, max: 1000, step: 0.01 }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Coefficient Draw"
                            type="number"
                            fullWidth
                            value={coefficientDraw}
                            onChange={(e) => setCoefficientDraw(e.target.value)}
                            inputProps={{ min: 1, max: 1000, step: 0.01 }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Coefficient Win 2"
                            type="number"
                            fullWidth
                            value={coefficientWin2}
                            onChange={(e) => setCoefficientWin2(e.target.value)}
                            inputProps={{ min: 1, max: 1000, step: 0.01 }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button variant="contained" onClick={onSave}>Save</Button>
                    </Grid>
                </Grid>
            )}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" component="div">{dialogMsg}</Typography>
                    {dialogHint !== '' ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>{dialogHint}</Typography>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
